import React, {FC, PropsWithChildren, useMemo, useState} from 'react';
import {navigate, useLocation} from '@reach/router';
import classNames from 'classnames';

import * as styles from './styles.module.scss';

interface TabProps {
	hash: string;
	title: string;
	className?: string;
	titleClassName?: string;
}

interface TabsProps {
	className?: string;
	defaultTab?: number
	hashToUrl?: boolean;
	onChange?: (index: number) => void
	children: React.FunctionComponentElement<TabProps>[];
	darkBg?: boolean;
}

const Tab: FC<PropsWithChildren<TabProps>> = ({children, className}) => {
	return (
		<div className={className}>
			{children}
		</div>
	);
};

const Tabs: FC<TabsProps> = ({
	className,
	defaultTab = 0,
	hashToUrl = true,
	onChange,
	children,
	darkBg,
}) => {
	const location = useLocation();
	const hashes = useMemo(() => (
		React.Children.map(children, (child) => child.props.hash)
	), []) || [];

	const [tab, setTab] = useState<number>(() => {
		const found = hashes.findIndex((hash) => decodeURI(location.hash).includes(hash));
		return found !== -1 ? found : defaultTab;
	});

	const onClick = (index: number) => {
		if(hashToUrl && hashes[index]) {
			navigate(`${location.pathname}#${hashes[index]}`, {replace: true});
		}
		if (onChange && index !== tab) {
			onChange(index);
		}
		setTab(index);
	};

	return (
		<div className={styles.container}>
			<ul className={classNames(styles.tabs, className)}>
				{React.Children.map(children, (child, index) => {
					const {title, titleClassName} = (child as React.FunctionComponentElement<TabProps>).props;
					return (
						<li
							className={classNames({
								[styles.tab]: true,
								[styles.dark]: darkBg,
								[titleClassName || '']: true,
								[styles.active]: tab === index,
							})}
							onClick={() => onClick(index)}
							key={index}
						>
							{title}
						</li>
					);
				})}
				<li
					className={classNames({
						[styles.tab]: true,
						[styles.stretch]: true,
						[styles.dark]: darkBg,
					})}
				/>
			</ul>
			<div>
				{React.Children.toArray(children)[tab]}
			</div>
		</div>
	);
};

export {Tab, Tabs};
